import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import ModalsManagerContext from "contexts/ModalsManager";
import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import ModalLayout, { DockPosition } from "components/ModalLayout";
import PDFViewer from "components/PDFViewer";
import { generateInvoicePdf } from "utils/generateInvoicePdf";
import usePDF from "hooks/usePDF";
import { NIPMaskedInput, AccountNumberMaskedInput } from "components/CreateRegistryEntryModal/MaskedInputs";
import { formatLive } from "components/CreateRegistryEntryModal/amountFormat";
import moment from "moment";
import axios from "axios";
import {
  Autocomplete,
  CircularProgress,
  debounce,
  Grid,
  InputAdornment,
  TextField,

  Typography,
} from "@mui/material";
import DropdownMui from "components/DropdownMui";
import { IDropdownObject } from "components/Dropdown/typings";
import { IInvoiceEntity } from "dto/Invoice/invoiceEntity";
import TrashIcon from "assets/icons/delete_24.svg?react";
import { DatePicker } from "@mui/x-date-pickers-pro";
import invoiceService from "api/invoiceService";
import { IInvoiceRow, InvoiceUnits, IVatRate } from "dto/Invoice/initInvoice";
import HollowButton from "components/HollowButton";
import BIRService from "api/BIRService";
import { IGUSCompany } from "dto/IGUSCompany";
import { isValidNip } from "utils/IsValidNip";
import { formatAddress } from "utils/formatAddress";
import { IPKWiU } from "dto/IPKWiU";
import { useTranslation } from "react-i18next";

interface IErrorMessage {
  title: string;
  message: string;
  hint?: string;
}

export const getInvoiceTypes = (
  t: (key: string) => string,
): IDropdownObject[] => [
  { value: 0, name: t("invoice.typeVAT") },
  { value: 1, name: t("invoice.typeNoVAT") },
  { value: 2, name: t("invoice.typeAdvance") },
  { value: 3, name: t("invoice.typeFinal") },
  { value: 4, name: t("invoice.typeCorrection") },
  { value: 5, name: t("invoice.typeDuplicate") },
  { value: 6, name: t("invoice.typeCorrectionNote") },
];

export const getPaymentTypes = (
  t: (key: string) => string,
): IDropdownObject[] => [
  { value: 0, name: t("invoice.paymentTransfer") },
  { value: 1, name: t("invoice.paymentCard") },
  { value: 2, name: t("invoice.paymentCash") },
];

export const getUnits = (t: (key: string) => string): IDropdownObject[] => [
  { value: InvoiceUnits.Piece, name: t("invoice.unitPiece") },
  { value: InvoiceUnits.Hour, name: t("invoice.unitHour") },
  { value: InvoiceUnits.Kg, name: t("invoice.unitKg") },
  { value: InvoiceUnits.Km, name: t("invoice.unitKm") },
  { value: InvoiceUnits.Liter, name: t("invoice.unitLiter") },
  { value: InvoiceUnits.Meter, name: t("invoice.unitMeter") },
  { value: InvoiceUnits.SquareMeter, name: t("invoice.unitSquareMeter") },
  { value: InvoiceUnits.CubicMeter, name: t("invoice.unitCubicMeter") },
  { value: InvoiceUnits.Ton, name: t("invoice.unitTon") },
  { value: InvoiceUnits.Service, name: t("invoice.unitService") },
];

// Note: DropdownMui treats an option value of 0 as "no selection" (it falls
// back to its empty-state placeholder), so the 0% rate must use a non-zero Id.
const VAT_RATES: IVatRate[] = [
  { Id: 23, Symbol: "23%", Name: "23%", Rate: 0.23 },
  { Id: 8, Symbol: "8%", Name: "8%", Rate: 0.08 },
  { Id: 5, Symbol: "5%", Name: "5%", Rate: 0.05 },
  { Id: 100, Symbol: "0%", Name: "0%", Rate: 0 },
];


// Utility compute helpers
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const formatFinancial = (value: number) =>
  (Number(value) || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function computeFromNetto(
  netto,
  quantity,
  vatRateId: number,
  vatRates: IVatRate[],
): Partial<IInvoiceRow> {
  var vatRate = vatRates?.find((x) => x.Id === vatRateId);

  if (!vatRate) {
    return {};
  }

  const totalNetto = netto * quantity;
  const vat = round2(totalNetto * vatRate.Rate);
  const total = round2(totalNetto + vat);
  return {
    Netto: round2(netto),
    TotalNetto: totalNetto,
    Vat: vat,
    Total: total,
  };
}

const fetchPkwiu = debounce(
  async (
    request: { input: string },
    callback: (results?: readonly IPKWiU[]) => void,
  ) => {
    const { data } = await axios.get(
      import.meta.env.VITE_APP_API_URL +
        `/ksef/invoice/pkwiu?search=${request.input}`,
    );

    callback(data);
  },
  400,
);

const InvoiceRow = ({
  index,
  row,
  vatRates,
  nameSuggestions,
  autoFocus,
  updateRow,
}: // handleChangeIsMain,
// handleCancel,
{
  index: number;
  row: IInvoiceRow;
  vatRates: IVatRate[];
  nameSuggestions: string[];
  autoFocus?: boolean;
  updateRow(id: string, row: Partial<IInvoiceRow>): void;
  handleRemove?(id: string): void;
  // handleChangeIsMain(): void;
  // handleCancel(): void;
}) => {
  const { t } = useTranslation();
  const comboVatRates = useMemo(
    () =>
      vatRates?.map(
        (x) =>
          ({
            name: `${Math.round(x.Rate * 100)}%`,
            value: x.Id,
          } as IDropdownObject),
      ),
    [vatRates],
  );

  const [pkwiu, setPkwiu] = useState<IPKWiU>(null);
  const [isNettoFocused, setIsNettoFocused] = useState(false);
  // Wyświetlana (formatowana na żywo) wartość pola netto + ref do kursora.
  const [nettoDisplay, setNettoDisplay] = useState("");
  const nettoRef = useRef<HTMLInputElement>(null);
  const [options, setOptions] = useState<readonly IPKWiU[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [fetchingPkwiu, setFetchingPkwiu] = useState(false);

  useEffect(() => {
    if (inputValue === "") {
      setOptions(pkwiu ? [pkwiu] : []);
      return undefined;
    }

    if (inputValue.length < 3) {
      setOptions(pkwiu ? [pkwiu] : []);
      return undefined;
    }

    setFetchingPkwiu(true);
    // Allow to resolve the out of order request resolution.
    let active = true;

    fetchPkwiu({ input: inputValue }, (results?: readonly IPKWiU[]) => {
      setFetchingPkwiu(false);
      if (!active) {
        return;
      }

      setOptions(results ?? []);
    });

    return () => {
      active = false;
    };
  }, [pkwiu, inputValue]);

  const handleBlur = () => {
    // updateRow(row.id, { errors });
  };

  const recalcFromQuantity = (
    row: IInvoiceRow,
    quantity,
  ): Partial<IInvoiceRow> => {
    const newValues = computeFromNetto(
      row.Netto,
      quantity,
      row.VatRate,
      vatRates,
    );

    return newValues;
  };

  const recalcFromNet = (row: IInvoiceRow, net): Partial<IInvoiceRow> => {
    const newValues = computeFromNetto(
      net,
      row.Quantity,
      row.VatRate,
      vatRates,
    );

    return newValues;
  };

  return (
    <div className={styles.position}>
      <div className={styles.plp}>{index + 1}</div>
      <div className={styles.pname}>
        <Autocomplete
          freeSolo
          openOnFocus
          disableClearable
          size="small"
          fullWidth
          options={nameSuggestions}
          inputValue={row.Description}
          onInputChange={(_, value) =>
            updateRow(row.Id, { Description: value })
          }
          onBlur={() => handleBlur()}
          componentsProps={{
            paper: { className: styles.nameSuggestions },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              variant="standard"
              multiline
              autoFocus={autoFocus}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
            />
          )}
        />
      </div>
      <div className={styles.ppkwiu}>
        <Autocomplete
          disableClearable
          size="small"
          sx={{ width: "100%" }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.Symbol
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={pkwiu}
          noOptionsText={t("common.typeToSearch")}
          clearIcon={undefined}
          onChange={(event: any, newValue: IPKWiU | null) => {
            setOptions(newValue ? [newValue, ...options] : options);
            setPkwiu(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          componentsProps={{
            paper: {
              sx: {
                width: 400,
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              variant="outlined"
              style={{
                width: "100%",
                // height: height,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      {fetchingPkwiu ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        <></>
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: null,
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li key={option.Id} {...optionProps}>
                <Grid>
                  {option.Symbol}
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {option.Name}
                  </Typography>
                </Grid>
              </li>
            );
          }}
        />
      </div>
      <div className={styles.pcount}>
        <input
          inputMode="numeric"
          maxLength={5}
          value={row.Quantity || ""}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
            const q = digits === "" ? 0 : Number(digits);
            const calc = recalcFromQuantity(row, q);
            updateRow(row.Id, { Quantity: q, ...calc });
          }}
        ></input>
      </div>
      <div className={styles.punit}>
        <DropdownMui
          list={getUnits(t)}
          placeholder="..."
          allowNull={true}
          defaultValue={row.Unit}
          onChange={(r) => updateRow(row.Id, { Unit: r.value as number })}
          hideBorder={true}
          nullName=""
          size="small"
          showBackground={false}
        ></DropdownMui>
      </div>
      <div className={styles.pnetto}>
        <input
          ref={nettoRef}
          value={
            isNettoFocused
              ? nettoDisplay
              : row.Netto
              ? formatFinancial(row.Netto)
              : ""
          }
          onFocus={() => {
            setNettoDisplay(row.Netto ? formatLive(String(row.Netto)).display : "");
            setIsNettoFocused(true);
          }}
          onBlur={() => setIsNettoFocused(false)}
          onChange={(e) => {
            const input = e.target;
            const caret = input.selectionStart ?? input.value.length;
            const sigBefore = input.value.slice(0, caret).replace(/\s/g, "").length;
            const { display, stored } = formatLive(input.value);
            setNettoDisplay(display);
            const net = Number(stored) || 0;
            const calc = recalcFromNet(row, net);
            updateRow(row.Id, { Netto: net, ...calc });
            requestAnimationFrame(() => {
              const el = nettoRef.current;
              if (!el) return;
              let sig = 0;
              let pos = 0;
              for (; pos < display.length; pos++) {
                if (display[pos] !== " ") sig++;
                if (sig >= sigBefore) {
                  pos++;
                  break;
                }
              }
              el.setSelectionRange(pos, pos);
            });
          }}
        ></input>
      </div>
      <div className={styles.pnetto}>
        {row.TotalNetto !== 0 ? formatFinancial(row.TotalNetto) : ""}
      </div>
      <div className={`${styles.pvat} ${styles.pvatRate}`}>
        {comboVatRates && (
          <DropdownMui
            list={comboVatRates}
            placeholder="..."
            defaultValue={row.VatRate}
            onChange={(r) => {
              const vatRateId = r.value as number;
              const calc = computeFromNetto(
                row.Netto,
                row.Quantity,
                vatRateId,
                vatRates,
              );
              updateRow(row.Id, { VatRate: vatRateId, ...calc });
            }}
            hideBorder={true}
            nullName=""
            size="small"
            showBackground={false}
          ></DropdownMui>
        )}
      </div>
      <div className={styles.pvat}>
        {row.Vat !== 0 ? formatFinancial(row.Vat) : ""}
      </div>
      <div className={styles.ptotal}>
        {row.Total !== 0 ? formatFinancial(row.Total) : ""}
      </div>
      <div className={styles.pactions}>
        <TrashIcon
          className={styles.removeIcon}
          onClick={() => handleRemove(row.Id)}
        />
      </div>
    </div>
  );
};

const CreateInvoiceModal: FC<Props> = () => {
  const { t } = useTranslation();

  const { data: initialData } = invoiceService.useInitInvoice();
  const { mutate: createInvoice } = invoiceService.useCreateInvoice();
  const [buyerSearch, setBuyerSearch] = useState("");
  const [sellerSearch, setSellerSearch] = useState("");

  const { data: birData, refetch: refetchBir } =
    BIRService.useGetCompanyFromNIP(buyerSearch);

  const cleanSellerNip = sellerSearch.replace(/\s/g, "");

  const {
    data: sellerBirData,
    refetch: refetchSellerBir,
    isLoading: isLoadingSellerBir,
  } = BIRService.useGetCompanyFromNIP(cleanSellerNip);

  const company: IGUSCompany = useMemo(
    () => (birData?.data as IGUSCompany) ?? undefined,
    [birData],
  );

  const sellerCompany: IGUSCompany = useMemo(
    () => (sellerBirData?.data as IGUSCompany) ?? undefined,
    [sellerBirData],
  );

  const outSideRef = useRef(null);

  const [type, setType] = useState<number>(0);
  const [number, setNumber] = useState("");
  const [sellerAccount, setSellerAccount] = useState("");

  const [buyer, setBuyer] = useState<IInvoiceEntity>({
    Name: "",
    Nip: "",
    Address: "",
    Email: "",
  });
  const [seller, setSeller] = useState<IInvoiceEntity>({
    Name: "",
    Nip: "",
    Address: "",
    Email: "",
  });

  useEffect(() => {
    if (isValidNip(buyerSearch)) {
      refetchBir();
    }
  }, [buyerSearch]);

  useEffect(() => {
    if (company) {
      setBuyer({
        Name: company.Name,
        Nip: buyerSearch,
        Address: company.Place
          ? formatAddress({
              place: company.Place,
              buildingNumber: company.HouseNumber,
              localNumber: company.LocalNumber,
              street: company.Street,
              zipCode: company.ZipCode,
            })
          : null,
        Email: "",
      });
    }
  }, [company]);

  useEffect(() => {
    if (isValidNip(sellerSearch)) {
      refetchSellerBir();
    }
  }, [sellerSearch]);

  useEffect(() => {
    if (sellerCompany) {
      setSeller({
        Name: sellerCompany.Name,
        Nip: cleanSellerNip,
        Address: sellerCompany.Place
          ? formatAddress({
              place: sellerCompany.Place,
              buildingNumber: sellerCompany.HouseNumber,
              localNumber: sellerCompany.LocalNumber,
              street: sellerCompany.Street,
              zipCode: sellerCompany.ZipCode,
            })
          : null,
        Email: "",
      });
    }
  }, [sellerCompany]);

  const vatRates = VAT_RATES;

  const [paymentType, setPaymentType] = useState<number>(0);
  const [creationDate, setCreationDate] = useState<Date>(new Date());
  const [sellDate, setSellDAte] = useState<Date>(undefined);
  const [paymentDate, setPaymentDate] = useState<Date>(
    moment(new Date()).add(14, "days").toDate(),
  );
  const [note, setNote] = useState("");

  const emptyRow = () => ({
    Id: crypto.randomUUID(),
    Description: "",
    Quantity: 1,
    Netto: 0,
    Total: 0,
    TotalNetto: 0,
    Unit: InvoiceUnits.Piece,
    Vat: 0,
    VatRate: vatRates && vatRates.length ? vatRates[0].Id : -1,
  });

  const [rows, setRows] = useState<IInvoiceRow[]>([emptyRow()]);
  const [lastAddedRowId, setLastAddedRowId] = useState<string | null>(null);

  const invoiceTotals = rows.reduce(
    (acc, r) => {
      acc.net += r.TotalNetto;
      acc.vat += r.Vat;
      acc.total += r.Total;
      return acc;
    },
    { net: 0, vat: 0, total: 0 },
  );

  const vatSummaryByRate = VAT_RATES.filter((rate) => rate.Rate > 0).map(
    (rate) => ({
      rate,
      vat: rows
        .filter((r) => r.VatRate === rate.Id)
        .reduce((sum, r) => sum + (r.Vat || 0), 0),
    }),
  );

  const [errorMesssage, setErrorMessage] = useState<IErrorMessage>(null);

  const [isLoading, setIsLoading] = useState(false);

  const { modals, setModals } = useContext(ModalsManagerContext);

  const isDisabled = () => {
    // if (!participants.length) {
    //   return true;
    // }

    return false;
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const { blob: previewBlob, addBlob: showPreview, removeBlob: closePreview } = usePDF();

  const handlePreview = async () => {
    const pdfBlob = await generateInvoicePdf({
      seller,
      buyer,
      invoiceNumber: number,
      invoiceType: type,
      creationDate,
      sellDate,
      paymentDate,
      paymentType,
      rows,
      vatRates,
      note,
    });
    showPreview(pdfBlob, "Podgląd faktury.pdf");
  };

  const handleSend = async () => {
    setIsLoading(true);

    createInvoice(
      {
        Date: creationDate,
        Paymentdate: paymentDate,
        Rows: rows.map((row) => {
          const vatRateObj = vatRates.find((r) => r.Id === row.VatRate);
          return {
            ...row,
            VatRate: vatRateObj !== undefined ? Math.round(vatRateObj.Rate * 100) : row.VatRate,
          };
        }),
        Note: note,
        Buyer: {
          Name: buyer?.Name,
          Nip: buyer?.Nip,
          Address: buyer?.Address,
          Email: buyer.Email,
        },
      },
      {
        onSuccess() {
          setModals({ ...modals, createInvoice: false });
        },
        onError(e: { response?: { data?: { errorCodes?: Array<{ message?: string }> } } }) {
          setIsLoading(false);
          const beMessage = e?.response?.data?.errorCodes?.[0]?.message;
          setErrorMessage({
            title: t("errors.somethingWentWrongTitle"),
            message: beMessage ?? t("errors.somethingWentWrong"),
          });
        },
      },
    );
  };

  const refWrapper = useRef(null);

  const inputsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef(null);

  const handleCloseModal = () => {
    setModals({ ...modals, hide: true });
  };

  const handleRowRemove = (id: string) => {
    setRows((r) => r.filter((x) => x.Id !== id));
  };

  const handleRowUpdate = (id: string, row: Partial<IInvoiceRow>) => {
    setRows((prev) => prev.map((r) => (r.Id === id ? { ...r, ...row } : r)));
  };

  const handleAddRow = () => {
    const newRow = emptyRow();
    setRows((r) => [...r, newRow]);
    setLastAddedRowId(newRow.Id);
  };

  const nameSuggestions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.Description?.trim()).filter((d) => !!d)),
      ),
    [rows],
  );

  useEffect(() => {
    if (initialData?.data) {
      if (initialData.data.Seller) {
        setSeller({
          Name: initialData.data.Seller.Name,
          Nip: initialData.data.Seller.NIP,
          Address: initialData.data.Seller.Address,
          Email: initialData.data.Seller.Email,
        });
      }
    }
  }, [initialData?.data]);

  return (
    <>
      <ModalLayout
        name="createInvoice"
        dock={DockPosition.DISABLED}
        customX={8}
        customY={0}
        ignore
        refWrapper={refWrapper}
        isAnimation
        widthAsLeftArea={true}
        disableMove={true}
        fullHeight={true}
      >
        <>
          <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.title}>
              {t("invoice.newInvoice")}
              <div className={styles.icons}>
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={handleCloseModal}
                />
              </div>
            </div>
            <div className={styles.contentWrapper}>
              {/* Wiersz 1: Konto Sprzedawcy | Rodzaj faktury + Numer faktury */}
              <div className={styles.topRow}>
                <div className={styles.topRowLeft}>
                  <TextField
                    label="Konto Sprzedawcy"
                    value={sellerAccount}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setSellerAccount(e.target.value)}
                    InputProps={{
                      inputComponent: AccountNumberMaskedInput as React.ElementType,
                    }}
                  />
                </div>
                <div className={styles.topRowRight}>
                  <DropdownMui
                    list={getInvoiceTypes(t)}
                    label={t("invoice.invoiceType")}
                    placeholder={t("invoice.invoiceType")}
                    allowNull={false}
                    defaultValue={type}
                    onChange={(r) => setType(r.value as number)}
                    variant="outlined"
                  />
                  <TextField
                    label={t("invoice.invoiceNumber")}
                    value={number}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setNumber(e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.line}></div>

              {/* Sprzedawca | Nabywca */}
              <div className={styles.entityRow}>
                <fieldset className={styles.entityBox}>
                  <legend className={styles.entityLabel}>Sprzedawca</legend>
                  <TextField
                    label="NIP"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={sellerSearch}
                    onChange={(e) => setSellerSearch(e.target.value)}
                    InputProps={{
                      inputComponent: NIPMaskedInput as React.ElementType,
                      ...(isLoadingSellerBir && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ),
                      }),
                    }}
                  />
                  <TextField
                    label="Firma"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={seller.Name}
                    onChange={(e) => setSeller({ ...seller, Name: e.target.value })}
                  />
                  <TextField
                    label="Adres"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={seller.Address ?? ""}
                    onChange={(e) => setSeller({ ...seller, Address: e.target.value })}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={seller.Email}
                    onChange={(e) => setSeller({ ...seller, Email: e.target.value })}
                  />
                </fieldset>

                <fieldset className={styles.entityBox}>
                  <legend className={styles.entityLabel}>Nabywca</legend>
                  <TextField
                    label="NIP"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={buyerSearch}
                    onChange={(e) => setBuyerSearch(e.target.value)}
                  />
                  <TextField
                    label="Firma"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={buyer.Name}
                    onChange={(e) => setBuyer({ ...buyer, Name: e.target.value })}
                  />
                  <TextField
                    label="Adres"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={buyer.Address ?? ""}
                    onChange={(e) => setBuyer({ ...buyer, Address: e.target.value })}
                  />
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    variant="standard"
                    value={buyer.Email}
                    onChange={(e) => setBuyer({ ...buyer, Email: e.target.value })}
                  />
                </fieldset>
              </div>

              <div className={styles.line}></div>

              <div className={styles.invHeader}>
                <div className={styles.dateRow}>
                  <DatePicker
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        size: "small",
                        placeholder: t("invoice.issueDate"),
                      },
                    }}
                    value={creationDate ? moment(creationDate) : undefined}
                    className={`${styles.picker} ${styles.pickerNarrow}`}
                    format="DD-MM-YYYY"
                    label={t("invoice.issueDate")}
                    onChange={(e: any) => {
                      setCreationDate(moment(e).toDate());
                    }}
                  />
                  <DatePicker
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        size: "small",
                        placeholder: t("invoice.saleDate"),
                      },
                    }}
                    value={creationDate ? moment(sellDate) : undefined}
                    className={`${styles.picker} ${styles.pickerNarrow}`}
                    format="DD-MM-YYYY"
                    label={t("invoice.saleDate")}
                    onChange={(e: any) => {
                      setSellDAte(moment(e).toDate());
                    }}
                  />
                  <DatePicker
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        size: "small",
                        placeholder: t("invoice.paymentDeadline"),
                        sx: { width: 175, minWidth: 175 },
                      },
                    }}
                    value={creationDate ? moment(paymentDate) : undefined}
                    className={styles.picker}
                    format="DD-MM-YYYY"
                    label={t("invoice.paymentDeadline")}
                    onChange={(e: any) => {
                      setPaymentDate(moment(e).toDate());
                    }}
                  />
                </div>
                <DropdownMui
                  className={styles.paymentMethod}
                  list={getPaymentTypes(t)}
                  label={t("invoice.paymentMethod")}
                  placeholder={t("invoice.paymentMethod")}
                  allowNull={false}
                  defaultValue={0}
                  onChange={(r) => setPaymentType(r.value as number)}
                  variant="outlined"
                ></DropdownMui>
              </div>

              <div className={styles.line}></div>

              <div className={styles.positions}>
                <div className={styles.header}>
                  <div className={styles.plp}>Lp.</div>
                  <div className={styles.pname}>{t("common.name")}</div>
                  <div className={styles.ppkwiu}>{t("invoice.colPkwiu")}</div>
                  <div className={styles.pcount}>
                    {t("invoice.colQuantity")}
                  </div>
                  <div className={styles.punit}>{t("invoice.colUnit")}</div>
                  <div className={styles.pnetto}>
                    {t("invoice.colNetPrice")}
                  </div>
                  <div className={styles.pnetto}>
                    {t("invoice.colNetValue")}
                  </div>
                  <div className={`${styles.pvat} ${styles.pvatRate}`}>
                    {t("invoice.colVatRate")}
                  </div>
                  <div className={styles.pvat}>{t("invoice.colVatAmount")}</div>
                  <div className={styles.ptotal}>
                    {t("invoice.colGrossValue")}
                  </div>
                  <div className={styles.pactions}></div>
                </div>

                <div className={styles.positionWrapper}>
                  {rows.map((x, index) => (
                    <InvoiceRow
                      key={x.Id}
                      index={index}
                      row={x}
                      vatRates={vatRates}
                      nameSuggestions={nameSuggestions}
                      autoFocus={x.Id === lastAddedRowId}
                      handleRemove={handleRowRemove}
                      updateRow={handleRowUpdate}
                    ></InvoiceRow>
                  ))}
                  <div className={styles.addButtonRow}>
                    <HollowButton
                      text={t("invoice.addPosition")}
                      onClick={handleAddRow}
                    ></HollowButton>
                  </div>

                  <div className={`${styles.total} ${styles.totalRow}`}>
                    <div className={styles.plp}></div>
                    <div className={styles.pname}></div>
                    <div className={styles.ppkwiu}></div>
                    <div className={styles.pcount}></div>
                    <div className={styles.punit}></div>
                    <div className={styles.pnetto}>
                      <b>{t("invoice.total")}:</b>
                    </div>
                    <div className={styles.pnetto}>
                      {invoiceTotals.net !== 0
                        ? formatFinancial(invoiceTotals.net)
                        : ""}
                    </div>
                    <div className={`${styles.pvat} ${styles.pvatRate}`}></div>
                    <div className={styles.pvat}>
                      {invoiceTotals.vat !== 0
                        ? formatFinancial(invoiceTotals.vat)
                        : ""}
                    </div>
                    <div className={styles.ptotal}>
                      {invoiceTotals.total !== 0
                        ? formatFinancial(invoiceTotals.total)
                        : ""}
                    </div>
                    <div className={styles.pactions}></div>
                  </div>

                  {vatSummaryByRate.map(({ rate, vat }, summaryIndex) => (
                    <div
                      className={`${styles.total} ${styles.vatSummaryRow}`}
                      key={rate.Id}
                    >
                      <div className={styles.plp}></div>
                      <div className={styles.pname}></div>
                      <div className={styles.ppkwiu}></div>
                      <div className={styles.pcount}></div>
                      <div className={styles.punit}></div>
                      <div className={styles.pnetto}></div>
                      <div className={styles.pnetto}>
                        {summaryIndex === 0 && (
                          <b>{t("invoice.vatSummaryIncluded")}</b>
                        )}
                      </div>
                      <div className={`${styles.pvat} ${styles.pvatRate}`}>
                        {Math.round(rate.Rate * 100)}%
                      </div>
                      <div className={styles.pvat}>
                        {vat !== 0 ? formatFinancial(vat) : ""}
                      </div>
                      <div className={styles.ptotal}></div>
                      <div className={styles.pactions}></div>
                    </div>
                  ))}
                </div>
              </div>

              <TextField
                className={styles.notes}
                label={t("forms.notes")}
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              <div className={styles.inputs}>
                <div ref={inputsRef} className={styles.inputsWrapper}>
                  <div className={styles.inputRow}>
                    <div
                      className={`${styles.inputControl} ${styles.dropdownParent}`}
                      ref={outSideRef}
                    ></div>
                  </div>
                </div>
              </div>

              <div className={styles.footer}>
                <HollowButton
                  onClick={handlePreview}
                  text={t("common.preview")}
                />
                <Button
                  loading={isLoading}
                  className={styles.btn2}
                  disabled={isDisabled()}
                  onClick={handleSend}
                  element={<>{t("common.create")}</>}
                />
              </div>

              {errorMesssage && (
                <div className={styles.error}>
                  <div className={styles.icons}>
                    <CloseIcon
                      className={styles.closeIcon}
                      onClick={handleCloseError}
                    />
                  </div>
                  <div className={styles.inner}>
                    <div className={styles.header}>{errorMesssage.title}</div>
                    <div className={styles.message}>
                      {errorMesssage.message}
                    </div>
                    <div className={styles.hint}>{errorMesssage.hint}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      </ModalLayout>
      {previewBlob &&
        ReactDOM.createPortal(
          <div className={styles.pdfPreviewPanel}>
            <PDFViewer
              file={previewBlob}
              title="Podgląd faktury"
              showCloseButton={true}
              onCloseButton={closePreview}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default CreateInvoiceModal;
